import { Quote, Star, TrendingUp, ArrowLeft, Sparkles } from "lucide-react";
import SectionHeading, { SectionDivider } from "@/components/SectionHeading";

const STORIES_AR = [
  {
    id: "sarah-uiux",
    name: "سارة ·",
    role: "مصممة UI/UX",
    city: "الرياض",
    rating: 5,
    before: "3,000 ريال/شهر",
    after: "25,000 ريال/شهر",
    duration: "خلال 14 شهر",
    quote:
      "بديت فريلانسر بالتصميم وأسعاري عشوائية. أول ما استخدمت حاسبة برولانسر، فهمت القيمة الحقيقية لخدمتي ورفعت سعر الساعة من 50 لـ 220 ريال خلال سنة.",
  },
  {
    id: "mohammed-dev",
    name: "محمد ·",
    role: "مطوّر ويب",
    city: "جدة",
    rating: 5,
    before: "0 ريال",
    after: "18,000 ريال/شهر",
    duration: "خلال 8 شهور",
    quote:
      "درست برمجة ذاتيًا وبعدها حسّيت ضايع في التسعير. حاسبة أسعار العمل الحر خلتني أعرض على عميلي الأول عرض سعر محترف بـ 4,500 ريال، ومن هذاك اليوم ما وقفت.",
  },
  {
    id: "noor-content",
    name: "نور ·",
    role: "كاتبة محتوى",
    city: "الدمام",
    rating: 5,
    before: "1,200 ريال/شهر",
    after: "12,000 ريال/شهر",
    duration: "خلال 10 شهور",
    quote:
      "كنت أكتب مقال كامل بـ 80 ريال! لمّا استخدمت حاسبة برولانسر وشفت أن السعر الحقيقي بين 300-900 ريال، غيّرت كل عملائي خلال 3 شهور ودخلي اتضاعف 10 مرات.",
  },
];

const STORIES_EN = [
  {
    id: "sarah-uiux",
    name: "Sarah",
    role: "UI/UX Designer",
    city: "Riyadh",
    rating: 5,
    before: "3,000 SAR/mo",
    after: "25,000 SAR/mo",
    duration: "in 14 months",
    quote:
      "I started with random pricing. The first time I used Prolancer calculator, I understood my service's real value and raised my hourly rate from 50 to 220 SAR within a year.",
  },
  {
    id: "mohammed-dev",
    name: "Mohammed",
    role: "Web Developer",
    city: "Jeddah",
    rating: 5,
    before: "0 SAR",
    after: "18,000 SAR/mo",
    duration: "in 8 months",
    quote:
      "I was self-taught and lost on pricing. The Prolancer calculator helped me pitch my first client with a confident 4,500 SAR proposal — and I haven't stopped since.",
  },
  {
    id: "noor-content",
    name: "Noor",
    role: "Content Writer",
    city: "Dammam",
    rating: 5,
    before: "1,200 SAR/mo",
    after: "12,000 SAR/mo",
    duration: "in 10 months",
    quote:
      "I used to write a full article for 80 SAR! Once I saw real market rates in the Prolancer calculator (300-900 SAR), I switched my clients within 3 months and 10x'd my income.",
  },
];

export default function SuccessStories({ lang = "ar", theme = "dark" }) {
  const dark = theme === "dark";
  const ar = lang === "ar";
  const stories = ar ? STORIES_AR : STORIES_EN;

  return (
    <section
      id="stories"
      data-testid="success-stories-section"
      dir={ar ? "rtl" : "ltr"}
      className={`relative ${dark ? "bg-[#1f2a47]" : "bg-[#f3f8ff]"}`}
    >
      <SectionDivider theme={theme} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <SectionHeading
          lang={lang}
          theme={theme}
          icon={<Sparkles className="h-3.5 w-3.5" />}
          eyebrow={ar ? "قصص نجاح حقيقية" : "Real success stories"}
          title={
            ar
              ? "من الصفر إلى دخل مستدام مع حاسبة برولانسر"
              : "From zero to steady income with Prolancer"
          }
          subtitle={
            ar
              ? "مستقلون سعوديون يروون كيف غيّرت أدوات التسعير الذكية حياتهم المهنية — بدون شهادات معقّدة أو رأس مال."
              : "Saudi freelancers share how smart pricing tools changed their careers — no fancy degrees, no capital needed."
          }
          compact
        />

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
          {stories.map((s) => (
            <article
              key={s.id}
              data-testid={`story-${s.id}`}
              className={`relative rounded-2xl p-4 border transition-all hover:scale-[1.02] ${dark ? "bg-[#182138] border-[#2e3a5c] hover:border-[#18c98f]/60" : "bg-white border-[#e5e5ea] hover:border-[#18c98f]/60"}`}
            >
              <Quote className="absolute top-3 end-3 h-5 w-5 text-[#18c98f]/20" />
              <div className="flex items-center gap-0.5 text-[#18c98f]">
                {Array.from({ length: s.rating }).map((_, i) => (
                  <Star key={i} className="h-3 w-3 fill-current" />
                ))}
              </div>
              <div className="mt-2">
                <div className={`text-sm font-bold ${ar ? "font-arabic" : "font-outfit"} ${dark ? "text-white" : "text-[#182138]"}`}>
                  {s.name} <span className="text-[#18c98f]">{s.role}</span>
                </div>
                <div className={`text-[10px] mt-0.5 ${dark ? "text-white/50" : "text-[#2d2c35]/60"} ${ar ? "font-arabic" : "font-outfit"}`}>
                  {ar ? "📍 " : "📍 "}{s.city}
                </div>
              </div>

              {/* Before / After */}
              <div className={`mt-3 rounded-xl p-2 flex items-center gap-2 ${dark ? "bg-[#1f2a47]" : "bg-[#f3f8ff]"}`}>
                <div className="flex-1 text-center">
                  <div className={`text-[8px] font-bold uppercase tracking-widest ${dark ? "text-white/50" : "text-[#2d2c35]/60"}`}>
                    {ar ? "قبل" : "Before"}
                  </div>
                  <div className={`mt-0.5 text-[11px] font-mono-num font-bold ${dark ? "text-white/70" : "text-[#2d2c35]/80"}`} dir="ltr">
                    {s.before}
                  </div>
                </div>
                <ArrowLeft className={`h-4 w-4 shrink-0 text-[#18c98f] ${ar ? "" : "rotate-180"}`} />
                <div className="flex-1 text-center">
                  <div className={`text-[8px] font-bold uppercase tracking-widest text-[#18c98f]`}>
                    {ar ? "الآن" : "Now"}
                  </div>
                  <div className={`mt-0.5 text-[11px] font-mono-num font-extrabold text-[#18c98f]`} dir="ltr">
                    {s.after}
                  </div>
                </div>
              </div>
              <div className={`mt-1 text-center text-[9px] font-mono-num ${dark ? "text-white/50" : "text-[#2d2c35]/60"}`}>
                <TrendingUp className="inline h-2.5 w-2.5 me-1" />
                <span dir="ltr">{s.duration}</span>
              </div>

              {/* Quote */}
              <blockquote
                className={`mt-3 text-[12px] leading-snug ${ar ? "font-arabic" : "font-outfit"} ${dark ? "text-white/80" : "text-[#2d2c35]"}`}
              >
                "{s.quote}"
              </blockquote>
            </article>
          ))}
        </div>

        <div className="mt-6 text-center">
          <a
            href="https://prolancer.sa"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="stories-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#18c98f] text-white text-xs font-extrabold font-arabic hover:bg-[#18c98f] transition shadow-[0_10px_30px_-10px_rgba(24,201,143,0.6)]"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {ar ? "ابدأ قصتك مع برولانسر" : "Start your story with Prolancer"}
          </a>
        </div>
      </div>
    </section>
  );
}
